import type { SignalIconKey } from '../profile/signal-source';

/**
 * The signal sources an audience listens to, and the rules for picking them — shared by
 * the filters drawer and the audience page's sources chip, which write the same answer to
 * two places (the drawer's draft, the open audience).
 *
 * Keys are the journey origins (NPJB-714): the server's own names once `dz_email` +
 * `dz_px_email` are folded into `email`, and so on. **Empty means all**: an audience that
 * stores `[]` listens to every source, which is also why the last one cannot go off.
 */

export type SignalSourceKey = 'email' | 'dz_publications' | 'social' | 'web_views';

export interface SignalSourceOption {
  key: SignalSourceKey;
  label: string;
  /** Who a signal is resolved to. */
  grain: string;
  description: string;
  /** The journeys' glyph and tone for this source. */
  icon: SignalIconKey;
}

/** In the journeys' display order, so every list of sources reads the same way. */
export const SIGNAL_SOURCE_OPTIONS: readonly SignalSourceOption[] = [
  {
    key: 'email',
    label: 'Digitalzone Email',
    grain: 'Person level',
    description:
      'Opens and clicks on campaigns run through Digitalzone, resolved to the person who acted.',
    icon: 'email',
  },
  {
    key: 'dz_publications',
    label: 'Digitalzone Publications',
    grain: 'Person level',
    description:
      "Reads and downloads on Digitalzone's own titles and syndicated placements, resolved to a named person.",
    icon: 'publications',
  },
  {
    key: 'social',
    label: 'Social',
    grain: 'Person level',
    description:
      'Engagement with posts and pages on social platforms, resolved to the person who engaged.',
    icon: 'social',
  },
  {
    key: 'web_views',
    label: 'Open Web Research',
    grain: 'Account level',
    description:
      'Topic-relevant page views observed across the web. Resolved to the company, not to a person — which is why an account can be reading without anyone at it being named.',
    icon: 'webViews',
  },
];

export interface ComingSoonSource {
  label: string;
  grain: string;
  description: string;
}

/** Sources not built yet: listed after the ones that work, with nothing to switch. */
export const COMING_SOON_SOURCES: readonly ComingSoonSource[] = [
  {
    label: 'Website Visits',
    grain: 'Person level',
    description:
      'A pixel on your own site, reporting the pages each visitor reads. Resolved to the person, so a named buyer working through your pricing page is a signal on their record rather than a number in your analytics.',
  },
  {
    label: 'Advertising Campaigns',
    grain: 'Person level',
    description:
      'Impressions and clicks on the display and paid social you are already running, read back at the person who saw them. What the ad platform reports as reach becomes a named buyer on an account you are working.',
  },
  {
    label: 'Demand Gen Campaigns',
    grain: 'Person level',
    description:
      'Opens and clicks on the demand generation campaigns Digitalzone runs for you, resolved to the person who acted. The delivery your campaign already produces, arriving as signal on the same record.',
  },
];

const ALL_KEYS = SIGNAL_SOURCE_OPTIONS.map((option) => option.key);

/**
 * A stored selection as the rules read it: unknown keys dropped (a retired source must not
 * keep narrowing an audience), and every source on collapsed to `[]`.
 */
export const normalizeSources = (selected: readonly string[]): SignalSourceKey[] => {
  const known = ALL_KEYS.filter((key) => selected.includes(key));
  return known.length === ALL_KEYS.length ? [] : known;
};

export const isSourceOn = (selected: readonly string[], key: SignalSourceKey): boolean => {
  const on = normalizeSources(selected);
  return on.length === 0 || on.includes(key);
};

/** Switch one source; the last one on stays on, since an audience hearing nothing is broken. */
export const toggleSource = (
  selected: readonly string[],
  key: SignalSourceKey,
): SignalSourceKey[] => {
  const current = normalizeSources(selected);
  const on = current.length === 0 ? [...ALL_KEYS] : current;
  const next = on.includes(key) ? on.filter((k) => k !== key) : [...on, key];
  if (next.length === 0) return current;
  return normalizeSources(ALL_KEYS.filter((k) => next.includes(k)));
};

/** "All 4 sources", or "2 of 4 sources" once narrowed. */
export const sourcesSummary = (selected: readonly string[]): string => {
  const on = normalizeSources(selected);
  return on.length === 0
    ? `All ${ALL_KEYS.length} sources`
    : `${on.length} of ${ALL_KEYS.length} sources`;
};

/** NPJB-764 — the sources by name, for read-only recaps: "Email, Social", or "All sources". */
export const sourceNames = (selected: readonly string[]): string => {
  const on = normalizeSources(selected);
  if (on.length === 0) return 'All sources';
  return SIGNAL_SOURCE_OPTIONS.filter((option) => on.includes(option.key))
    .map((option) => option.label)
    .join(', ');
};

/** Whether two selections mean the same sources, whatever their order or spelling of "all". */
export const sameSources = (a: readonly string[], b: readonly string[]): boolean =>
  normalizeSources(a).join() === normalizeSources(b).join();
